import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Drawer,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import WorkflowAssignmentPanel from "../../shared/WorkflowAssignmentPanel";
import {
  useGetEligibleWorkflowsQuery,
  useCreateApiTokenMutation,
  useUpdateApiTokenMutation,
} from "../../../redux/api";

const INITIAL_FORM = {
  name: "",
  tokenType: "STATIC",
  apiAccess: "READ_WRITE",
  executeWorkflowScope: "ALL",
  assignedWorkflows: [],
};

function normalizeAccountKey(value) {
  if (value == null || value === "") return null;
  const s = String(value).trim().toLowerCase();
  return s === "all" ? null : String(value).trim();
}

export default function APITokenFormDrawer({ open, drawerTarget, onClose, onSaved, account }) {
  const [form, setForm] = useState(INITIAL_FORM);
  const [apiError, setApiError] = useState(null);

  const isNew = !!drawerTarget?.isNew;
  const isDynamic = form.tokenType === "DYNAMIC" || drawerTarget?.token_type === "DYNAMIC";
  const isAllMode = account?.toLowerCase() === "all";

  const workflowAccount = isAllMode
    ? (!isNew ? normalizeAccountKey(drawerTarget?.account) : null)
    : normalizeAccountKey(account);

  const showWorkflowAssignment =
    Boolean(workflowAccount) &&
    (isDynamic ? form.executeWorkflowScope === "SELECTED" : true);

  useEffect(() => {
    if (open && drawerTarget) {
      setForm({
        name: isNew ? "" : (drawerTarget.name || ""),
        tokenType: isNew ? (drawerTarget.tokenType || "STATIC") : (drawerTarget.token_type || "STATIC"),
        apiAccess: drawerTarget.api_access || "READ_WRITE",
        executeWorkflowScope: drawerTarget.execute_workflow_scope || "ALL",
        assignedWorkflows: isNew ? [] : (drawerTarget.assignedWorkflows || []),
      });
      setApiError(null);
    }
  }, [open, drawerTarget]); // eslint-disable-line react-hooks/exhaustive-deps

  const workflowsQuery = useGetEligibleWorkflowsQuery(
    { account: workflowAccount },
    {
      skip: !open || !showWorkflowAssignment,
      refetchOnMountOrArgChange: true,
    }
  );

  const workflowsOk =
    workflowsQuery.data?.current_state === "SUCCESS" ||
    workflowsQuery.data?.status === "SUCCESS";

  const availableWorkflows = workflowsOk ? (workflowsQuery.data?.data || []) : [];

  const [createApiToken, { isLoading: isCreating }] = useCreateApiTokenMutation();
  const [updateApiToken, { isLoading: isUpdating }] = useUpdateApiTokenMutation();
  const isSaving = isCreating || isUpdating;

  const handleClose = () => {
    setForm(INITIAL_FORM);
    setApiError(null);
    onClose();
  };

  const handleSave = async () => {
    setApiError(null);
    let result;

    if (isNew) {
      result = await createApiToken({
        tokenname: form.name.trim(),
        account,
        assignedWorkflows: form.assignedWorkflows,
        tokenType: form.tokenType,
        apiAccess: form.apiAccess,
        executeWorkflowScope: form.executeWorkflowScope,
      });
    } else {
      result = await updateApiToken({
        tokenid: drawerTarget.id,
        assignedWorkflows: form.assignedWorkflows,
        apiAccess: form.apiAccess,
        executeWorkflowScope: form.executeWorkflowScope,
      });
    }

    const ok =
      result.data?.current_state === "SUCCESS" ||
      result.data?.status === "SUCCESS";

    if (ok) {
      handleClose();
      onSaved?.();
    } else {
      setApiError(result.data?.message || result.error?.error || "Save failed. Please try again.");
    }
  };

  const drawerTitle = isNew
    ? (isDynamic ? "Add Dynamic Token" : "Add API Token")
    : (isDynamic ? "Edit Dynamic Token" : "Edit API Token");

  return (
    <Drawer anchor="left" open={open} onClose={handleClose}>
      <Box
        sx={{
          width: showWorkflowAssignment ? 760 : 520,
          maxWidth: "95vw",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          bgcolor: "#e9e9e9",
        }}
      >
        <Box sx={{ bgcolor: "#0f4c81", px: 1.25, py: 0.75 }}>
          <Typography sx={{ fontWeight: 800, color: "#d2d7e0" }}>{drawerTitle}</Typography>
        </Box>

        <Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>
          {apiError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setApiError(null)}>
              {apiError}
            </Alert>
          )}

          <Box sx={{ border: "1px solid #d5d9e0", borderRadius: 2, bgcolor: "#f8f9fc", overflow: "hidden", mb: 2 }}>
            <Box sx={{ px: 1.5, py: 1, borderBottom: "1px solid #e1e5ec", bgcolor: "#eef1f6" }}>
              <Typography sx={{ fontWeight: 700, color: "#5f6878", fontSize: "0.85rem", textTransform: "uppercase" }}>
                Token Details
              </Typography>
            </Box>
            <Box sx={{ p: 2 }}>
              {isNew && (
                <FormControl component="fieldset" sx={{ mb: 2 }}>
                  <FormLabel component="legend">Token Type</FormLabel>
                  <RadioGroup
                    row
                    value={form.tokenType}
                    onChange={(e) => setForm((f) => ({ ...f, tokenType: e.target.value }))}
                  >
                    <FormControlLabel value="STATIC" control={<Radio size="small" />} label="Static" />
                    <FormControlLabel value="DYNAMIC" control={<Radio size="small" />} label="Dynamic" />
                  </RadioGroup>
                </FormControl>
              )}

              <TextField
                size="small"
                fullWidth
                label="Token Name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                disabled={!isNew}
                required
                helperText={
                  isDynamic
                    ? "Dynamic tokens define permission profiles; no secret is generated."
                    : (!isNew ? "Token name cannot be changed after creation." : "")
                }
              />

              {isDynamic && (
                <>
                  <FormControl component="fieldset" sx={{ mt: 2 }}>
                    <FormLabel component="legend">API Access</FormLabel>
                    <RadioGroup
                      row
                      value={form.apiAccess}
                      onChange={(e) => setForm((f) => ({ ...f, apiAccess: e.target.value }))}
                    >
                      <FormControlLabel value="READ" control={<Radio size="small" />} label="Read" />
                      <FormControlLabel value="WRITE" control={<Radio size="small" />} label="Write" />
                      <FormControlLabel value="READ_WRITE" control={<Radio size="small" />} label="Read & Write" />
                    </RadioGroup>
                  </FormControl>

                  <FormControl component="fieldset" sx={{ mt: 1 }}>
                    <FormLabel component="legend">Execute Workflow</FormLabel>
                    <RadioGroup
                      row
                      value={form.executeWorkflowScope}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, executeWorkflowScope: e.target.value }))
                      }
                    >
                      <FormControlLabel value="ALL" control={<Radio size="small" />} label="All" />
                      <FormControlLabel value="SELECTED" control={<Radio size="small" />} label="Selected" />
                    </RadioGroup>
                  </FormControl>
                </>
              )}

              {isAllMode && showWorkflowAssignment && (
                <Typography variant="caption" sx={{ display: "block", mt: 1.5, color: "#5f6878" }}>
                  Workflow assignment applies to account <strong>{workflowAccount}</strong>.
                </Typography>
              )}
            </Box>
          </Box>

          {showWorkflowAssignment && (
            <Box sx={{ border: "1px solid #d5d9e0", borderRadius: 2, bgcolor: "#f8f9fc", overflow: "hidden" }}>
              <Box sx={{ px: 1.5, py: 1, borderBottom: "1px solid #e1e5ec", bgcolor: "#eef1f6" }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography sx={{ fontWeight: 700, color: "#5f6878", fontSize: "0.85rem", textTransform: "uppercase" }}>
                    Workflow Assignment
                  </Typography>
                  <Typography sx={{ fontSize: "0.75rem", color: "#8a93a2" }}>
                    {form.assignedWorkflows.length} assigned
                  </Typography>
                </Stack>
              </Box>
              <Box sx={{ p: 1.5 }}>
                <WorkflowAssignmentPanel
                  availableWorkflows={availableWorkflows}
                  assignedWorkflows={form.assignedWorkflows}
                  onChange={(updated) => setForm((f) => ({ ...f, assignedWorkflows: updated }))}
                  loading={workflowsQuery.isLoading || workflowsQuery.isFetching}
                />
              </Box>
            </Box>
          )}
        </Box>

        <Box sx={{ px: 2, py: 1.5, borderTop: "1px solid #d5d9e0", bgcolor: "#f4f5f8", display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Button variant="outlined" onClick={handleClose} disabled={isSaving}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!form.name.trim() || isSaving}
            onClick={handleSave}
            startIcon={isSaving ? <CircularProgress size={14} color="inherit" /> : null}
          >
            {isNew ? "Create" : "Save"}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}
